import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid } from '@mui/material';
import { TECHNICAL_POLICY_COLUMNS } from '../../../../services/technicalPolicy/technicalPolicyColumns';
import { TwoActionsDialogService } from '../../../../services/twoActionsDialog/twoActionsDialogService';
import { WizardUtils } from '../../../../utils/WizardUtils';
import EditableTable from '../../components/EditableTable';
import GenericAutoCompleteInput from '../../components/GenericAutoCompleteInput';
import { useSequenceActions } from './hooks/SequenceActionsHook';

const SequencesActionsTab = ({ selectedSequenceAction, setSelectedSequenceAction, selectedGisement, editMode }) => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();
  const {
    sequencesActions,
    coutsEtDurees,
    impacts,
    familleEquipement,
    niveauCout,
    tableNamesMapping,
    categoriesBudgetaires,
    tachesEnIntervention,
    tachesEnInterventionTable,
  } = useSequenceActions(selectedGisement);

  const [sequenceActionOptions, setSequenceActionOptions] = useState(
    WizardUtils.sortResourceListAlphabetically(Array.from(new Set(sequencesActions?.map((sa) => sa.SequenceActions))))
  );
  const [openCoutDureeModal, setOpenCoutDureeModal] = useState(false);
  const [openImpactModal, setOpenImpactModal] = useState(false);
  const [openTacheEnInterventionModal, setOpenTacheEnInterventionModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState('');
  const [updatedSequencesActions, setUpdatedSequencesActions] = useState(null);
  const openCoutsEtDureesModal = (actionId) => {
    setOpenCoutDureeModal(true);
    setSelectedActionId(actionId);
  };
  const openTachesEnInterventionModal = (actionId) => {
    setOpenTacheEnInterventionModal(true);
    setSelectedActionId(actionId);
  };

  const openImpactsModal = (actionId) => {
    setOpenImpactModal(true);
    setSelectedActionId(actionId);
  };

  const validateActionId = (row, allRows) => {
    if (!row.IdentifiantAction || row.IdentifiantAction.trim() === '') {
      return true;
    }
    return allRows?.some((saRow) => saRow.IdentifiantAction === row.IdentifiantAction && saRow.id !== row.id);
  };

  const parametresColumns = TECHNICAL_POLICY_COLUMNS.SequencesActions(
    (actionId) => openCoutsEtDureesModal(actionId),
    (actionId) => openTachesEnInterventionModal(actionId),
    (actionId) => openImpactsModal(actionId),
    categoriesBudgetaires,
    tachesEnIntervention,
    (row) => sequencesActions?.some((saRow) => saRow.IdentifiantAction === row.IdentifiantAction),
    (row, allRows) => validateActionId(row, allRows)
  );

  const handleNewActionSaved = (newRow) => {
    if (!newRow?.IdentifiantAction) return;

    const currentData = updatedSequencesActions || sequencesActions || [];
    const updatedData = [...currentData, newRow];
    setUpdatedSequencesActions(updatedData);

    const existingImpacts = getValues(tableNamesMapping.Impacts) ?? impacts ?? [];
    const newImpacts = WizardUtils.generateDefaultImpacts(newRow.IdentifiantAction, familleEquipement);
    setValue(tableNamesMapping.Impacts, [...existingImpacts, ...newImpacts]);

    const couts = WizardUtils.generateDefaultCoutsEtDurees(
      newRow.NiveauDefinitionCouts,
      newRow.IdentifiantAction,
      familleEquipement,
      niveauCout
    );
    const existingCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDurees ?? [];
    setValue(tableNamesMapping.CoutsEtDureesActions, [...existingCouts, ...couts]);
  };
  const handleAddSequenceAction = (newSequenceAction) => {
    setSelectedSequenceAction(newSequenceAction);
    setSequenceActionOptions((prevOptions) => [...prevOptions, newSequenceAction]);
  };

  const handleDeleteSequenceAction = async (event) => {
    if (selectedSequenceAction) {
      event.stopPropagation();
      const dialogProps = {
        id: 'delete-sequence-action',
        labels: {
          title: t('genericcomponent.common.confirmDialogs.deleteActionSequence.title', 'Delete action sequence?'),
          body: t(
            'genericcomponent.common.confirmDialogs.deleteActionSequence.body',
            'Actions, costs and durations as well as impacts associated with this sequence will also be deleted.'
          ),
          button1: t('genericcomponent.common.cancel', 'CANCEL'),
          button2: t('genericcomponent.common.delete', 'DELETE'),
        },
      };
      const result = await TwoActionsDialogService.openDialog(dialogProps);
      if (result === 2) setSelectedSequenceAction('');
      return result;
    }
  };
  const handleActionRowUpdate = async (newRow, oldRow) => {
    if (newRow.IdentifiantAction && newRow.NiveauDefinitionCouts !== oldRow.NiveauDefinitionCouts) {
      const dialogResult = await TwoActionsDialogService.openDialog({
        id: 'confirm-niveau-definition-change',
        labels: {
          title: t('genericcomponent.common.confirmDialogs.changeDefinitionLevel.title', 'Change definition level?'),
          body: t(
            'genericcomponent.common.confirmDialogs.changeDefinitionLevel.body',
            'This modification will result in the deletion of existing Costs and Durations. ' +
              'New default values will be generated.'
          ),
          button1: t('genericcomponent.common.cancel', 'CANCEL'),
          button2: t('genericcomponent.common.continue', 'CONTINUE'),
        },
      });
      if (dialogResult === 2) {
        const newCouts = WizardUtils.generateDefaultCoutsEtDurees(
          newRow.NiveauDefinitionCouts,
          newRow.IdentifiantAction,
          familleEquipement,
          niveauCout
        );
        const allCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDurees ?? [];
        const filtered = allCouts.filter((c) => c.IdentifiantAction !== newRow.IdentifiantAction);
        setValue(tableNamesMapping.CoutsEtDureesActions, [...filtered, ...newCouts]);
      } else {
        const allRows = getValues(tableNamesMapping.SequencesActions) ?? [];
        const updatedRows = allRows.map((row) =>
          row.id === newRow.id ? { ...row, NiveauDefinitionCouts: oldRow.NiveauDefinitionCouts } : row
        );
        setValue(tableNamesMapping.SequencesActions, updatedRows);
      }
    }
  };

  const handleDeleteAction = (deletedRow) => {
    if (!deletedRow || !deletedRow.IdentifiantAction) return;

    const actionId = deletedRow.IdentifiantAction;

    if (updatedSequencesActions) {
      const filteredSequencesActions = updatedSequencesActions.filter((item) => item.IdentifiantAction !== actionId);
      setUpdatedSequencesActions(filteredSequencesActions.length > 0 ? filteredSequencesActions : null);
    }

    const updatedCoutsEtDurees = coutsEtDurees.filter((item) => item.IdentifiantAction !== actionId);

    const updatedImpacts = impacts.filter((item) => item.IdentifiantAction !== actionId);

    if (tableNamesMapping.CoutsEtDureesActions) {
      setValue(tableNamesMapping.CoutsEtDureesActions, updatedCoutsEtDurees);
    }

    if (tableNamesMapping.Impacts) {
      setValue(tableNamesMapping.Impacts, updatedImpacts);
    }
  };

  const handleValueCopyChange = (valueCopy) => {
    setUpdatedSequencesActions(valueCopy);
  };

  const confirmDeleteDialogLabels = {
    id: 'delete-actions-sequence',
    labels: {
      title: t('genericcomponent.common.confirmDialogs.deleteActionSequence.title', 'Delete action sequence?'),
      body: t(
        'genericcomponent.common.confirmDialogs.deleteActionSequence.body',
        'Actions, costs and durations as well as impacts associated with this sequence will also be deleted.'
      ),
      button1: t('genericcomponent.common.cancel', 'CANCEL'),
      button2: t('genericcomponent.common.delete', 'DELETE'),
    },
  };

  return (
    <Box width={'100%'} sx={{ padding: 2 }}>
      <Controller
        name={tableNamesMapping.SequencesActions}
        defaultValue={sequencesActions ?? []}
        render={({ field }) => {
          const { value, onChange } = field;
          return (
            <>
              <Grid container sx={{ gap: 1, marginBottom: 2 }}>
                <Grid item>
                  <GenericAutoCompleteInput
                    id="select-or-create-actions-sequence-autocomplete"
                    label={t(
                      'genericcomponent.technicalPolicy.common.selectOrCreateActionSequence',
                      'Select or create an action sequence'
                    )}
                    value={selectedSequenceAction || ''}
                    options={sequenceActionOptions}
                    onChange={(event, newSequenceAction) => setSelectedSequenceAction(newSequenceAction)}
                    onNewOption={handleAddSequenceAction}
                    editMode={editMode}
                  />
                </Grid>
                <Grid item>
                  {selectedSequenceAction && editMode && (
                    <IconButton
                      data-cy="delete-sequence-action-button"
                      onClick={async (event) => {
                        const response = await handleDeleteSequenceAction(event);
                        if (response === 2) {
                          const sequenceActionToDelete = selectedSequenceAction;

                          const sequencesActionsBeforeDeletion =
                            getValues(tableNamesMapping.SequencesActions) ?? sequencesActions ?? [];
                          const filteredSequencesActions = sequencesActionsBeforeDeletion.filter(
                            (sa) => sa.SequenceActions !== sequenceActionToDelete
                          );
                          onChange(filteredSequencesActions);
                          const filteredOptions = sequenceActionOptions.filter(
                            (option) => option !== sequenceActionToDelete
                          );
                          setSequenceActionOptions(filteredOptions);
                          const actionsToDelete = sequencesActionsBeforeDeletion.filter(
                            (sa) => sa.SequenceActions === sequenceActionToDelete
                          );
                          const actionIdsToDelete = actionsToDelete.map((sa) => sa.IdentifiantAction);

                          const updatedCoutsEtDurees = (
                            getValues(tableNamesMapping.CoutsEtDureesActions) ??
                            coutsEtDurees ??
                            []
                          ).filter((item) => !actionIdsToDelete.includes(item.IdentifiantAction));

                          const updatedImpacts = (getValues(tableNamesMapping.Impacts) ?? impacts ?? []).filter(
                            (item) => !actionIdsToDelete.includes(item.IdentifiantAction)
                          );

                          if (tableNamesMapping.CoutsEtDureesActions) {
                            setValue(tableNamesMapping.CoutsEtDureesActions, updatedCoutsEtDurees);
                          }

                          if (tableNamesMapping.Impacts) {
                            setValue(tableNamesMapping.Impacts, updatedImpacts);
                          }
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
              <EditableTable
                id="actions-sequence"
                columns={parametresColumns.filter((col) => col.isInvisible !== true)}
                rows={value
                  .filter((action) => action.SequenceActions === selectedSequenceAction)
                  .sort((a, b) => (a.FamilleEquipementImpactee || '').localeCompare(b.FamilleEquipementImpactee || ''))}
                onChange={onChange}
                notFilteredRows={value}
                filteringCol={{ field: 'SequenceActions', value: selectedSequenceAction }}
                editMode={editMode && !!selectedSequenceAction}
                onDeleteRow={handleDeleteAction}
                confirmDeleteRow={true}
                confirmDeleteRowDialogProps={confirmDeleteDialogLabels}
                onNewRowSaved={handleNewActionSaved}
                addButtonAlignment="left"
                onRowUpdate={handleActionRowUpdate}
                onValueCopyChange={handleValueCopyChange}
              />
            </>
          );
        }}
      />

      {/* Modals for Coûts et Durées and Impacts */}
      <Dialog maxWidth={'xl'} fullWidth={true} open={openCoutDureeModal} onClose={() => setOpenCoutDureeModal(false)}>
        <DialogTitle>
          {t('genericcomponent.technicalPolicy.actionSequencesTable.costsAndDurations', 'Costs and durations')}
        </DialogTitle>
        <DialogContent>
          <Controller
            name={tableNamesMapping.CoutsEtDureesActions}
            defaultValue={coutsEtDurees}
            render={({ field }) => {
              const { value, onChange } = field;
              const formActions = getValues(tableNamesMapping.SequencesActions) ?? [];
              const selectedAction = formActions.find((a) => a.IdentifiantAction === selectedActionId);
              const niveauDefinitionCout = selectedAction?.NiveauDefinitionCouts ?? '';

              return (
                <EditableTable
                  id="cost-and-duration"
                  columns={TECHNICAL_POLICY_COLUMNS.CoutsEtDureesActions(
                    niveauCout,
                    familleEquipement,
                    niveauDefinitionCout
                  ).filter((col) => col.isInvisible !== true)}
                  rows={value?.filter((row) => row.IdentifiantAction === selectedActionId)}
                  onChange={onChange}
                  notFilteredRows={value}
                  filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
                  editMode={editMode}
                  canAddDeleteRows={false}
                  inlineEdit={true}
                />
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-cost-and-duration-dialog-button" onClick={() => setOpenCoutDureeModal(false)}>
            {t('genericcomponent.common.close', 'CLOSE')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth={'xl'}
        fullWidth={true}
        open={openTacheEnInterventionModal}
        onClose={() => setOpenTacheEnInterventionModal(false)}
      >
        <DialogTitle>
          {t('genericcomponent.technicalPolicy.actionSequencesTable.tasksInIntervention', 'Tasks in intervention')}
        </DialogTitle>
        <DialogContent>
          <Controller
            name={'tachesEnIntervention'}
            defaultValue={tachesEnInterventionTable}
            render={({ field }) => {
              const { value, onChange } = field;
              const filteredRows = value
                ?.filter((row) => {
                  return (updatedSequencesActions || sequencesActions)
                    ?.filter((sequenceAction) => sequenceAction.IdentifiantAction === selectedActionId)
                    .some((sequenceAction) => {
                      if (!sequenceAction.TachesEnIntervention) return false;
                      const tachesArray = sequenceAction.TachesEnIntervention.split('|');
                      return tachesArray.includes(row.Identifiant);
                    });
                })
                .sort((a, b) => (a.Identifiant || '').localeCompare(b.Identifiant || ''));

              return (
                <EditableTable
                  id="taches-en-intervention"
                  columns={TECHNICAL_POLICY_COLUMNS.TachesEnIntervention(familleEquipement).filter(
                    (col) => col.isInvisible !== true
                  )}
                  rows={filteredRows}
                  notFilteredRows={value}
                  filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
                  onChange={(newData) => {
                    onChange(newData);
                  }}
                  canAddDeleteRows={false}
                  hideActions={true}
                />
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-impacts-dialog-button" onClick={() => setOpenTacheEnInterventionModal(false)}>
            {t('genericcomponent.common.close', 'CLOSE')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth={'xl'} fullWidth={true} open={openImpactModal} onClose={() => setOpenImpactModal(false)}>
        <DialogTitle>{t('genericcomponent.technicalPolicy.actionSequencesTable.impacts', 'Impacts')}</DialogTitle>
        <DialogContent>
          <Controller
            name={tableNamesMapping.Impacts}
            defaultValue={impacts}
            render={({ field }) => {
              const { value, onChange } = field;
              return (
                <EditableTable
                  id="impacts"
                  columns={TECHNICAL_POLICY_COLUMNS.Impacts(familleEquipement).filter(
                    (col) => col.isInvisible !== true
                  )}
                  rows={value?.filter((row) => row.IdentifiantAction === selectedActionId)}
                  notFilteredRows={value}
                  filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
                  onChange={onChange}
                  canAddDeleteRows={false}
                  inlineEdit={true}
                />
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-impacts-dialog-button" onClick={() => setOpenImpactModal(false)}>
            {t('genericcomponent.common.close', 'CLOSE')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

SequencesActionsTab.propTypes = {
  selectedSequenceAction: PropTypes.string,
  setSelectedSequenceAction: PropTypes.func.isRequired,
  selectedGisement: PropTypes.string,
  editMode: PropTypes.bool,
};

export default SequencesActionsTab;
